/**
 * MINDMAP PROTOTYPE — the branch color system.
 *
 * Classic mindmap coloring: the root is brand-primary, and each FIRST-LEVEL
 * branch gets its own hue which every descendant (and the edges leading to
 * them) inherits. Hues come from the shared design-system ramps
 * (tailwind-preset.cjs), so everything is CSS-var driven and auto-inverts in
 * dark mode with no extra work.
 *
 * Tailwind classes must be complete string literals (JIT can't see
 * `bg-${color}-100`), hence the spelled-out palette entries. Edge strokes are
 * SVG `stroke` values, so they reference the same CSS vars directly.
 */

export type BranchColor = {
  /** Depth-1 node (the branch heading): tinted fill + stronger border. */
  branch: string;
  /** Depth-2+ node (sub-topics): faint fill + soft border. */
  leaf: string;
  /** SVG stroke for every edge inside this branch. */
  edge: string;
  /** PDF word-overlay highlight (split source view) in this branch's hue. */
  pdfHighlight: string;
  /** Same hue at very low opacity — the "other sections" wash in the split view. */
  pdfTint: string;
};

/**
 * Hue order is chosen so neighbouring branches contrast (blue → orange →
 * green → purple-pink → yellow → teal → red-pink → lime). Cycles past 8.
 */
export const BRANCH_COLORS: BranchColor[] = [
  {
    branch: 'border-sky-300 bg-sky-100',
    leaf: 'border-sky-200 bg-sky-50',
    edge: 'rgba(var(--sky-400-rgb), 1)',
    pdfHighlight: 'bg-sky-400/50',
    pdfTint: 'bg-sky-400/15',
  },
  {
    branch: 'border-orange-300 bg-orange-100',
    leaf: 'border-orange-200 bg-orange-50',
    edge: 'rgba(var(--orange-400-rgb), 1)',
    pdfHighlight: 'bg-orange-400/50',
    pdfTint: 'bg-orange-400/15',
  },
  {
    branch: 'border-emerald-300 bg-emerald-100',
    leaf: 'border-emerald-200 bg-emerald-50',
    edge: 'rgba(var(--emerald-400-rgb), 1)',
    pdfHighlight: 'bg-emerald-400/50',
    pdfTint: 'bg-emerald-400/15',
  },
  {
    branch: 'border-fuchsia-300 bg-fuchsia-100',
    leaf: 'border-fuchsia-200 bg-fuchsia-50',
    edge: 'rgba(var(--fuchsia-400-rgb), 1)',
    pdfHighlight: 'bg-fuchsia-400/50',
    pdfTint: 'bg-fuchsia-400/15',
  },
  {
    branch: 'border-amber-300 bg-amber-100',
    leaf: 'border-amber-200 bg-amber-50',
    edge: 'rgba(var(--amber-400-rgb), 1)',
    pdfHighlight: 'bg-amber-400/50',
    pdfTint: 'bg-amber-400/15',
  },
  {
    branch: 'border-teal-300 bg-teal-100',
    leaf: 'border-teal-200 bg-teal-50',
    edge: 'rgba(var(--teal-400-rgb), 1)',
    pdfHighlight: 'bg-teal-400/50',
    pdfTint: 'bg-teal-400/15',
  },
  {
    branch: 'border-rose-300 bg-rose-100',
    leaf: 'border-rose-200 bg-rose-50',
    edge: 'rgba(var(--rose-400-rgb), 1)',
    pdfHighlight: 'bg-rose-400/50',
    pdfTint: 'bg-rose-400/15',
  },
  {
    branch: 'border-lime-300 bg-lime-100',
    leaf: 'border-lime-200 bg-lime-50',
    edge: 'rgba(var(--lime-400-rgb), 1)',
    pdfHighlight: 'bg-lime-400/50',
    pdfTint: 'bg-lime-400/15',
  },
];

/** Root node + the short edge stubs leaving it (before a branch hue applies). */
export const ROOT_NODE_CLASS = 'border-primary-600 bg-primary-500 text-white';
export const ROOT_PDF_HIGHLIGHT = 'bg-primary-400/50';
export const ROOT_PDF_TINT = 'bg-primary-400/15';
export const ROOT_EDGE_COLOR = 'rgba(var(--primary-300-rgb), 1)';

export function branchColor(index: number): BranchColor {
  return BRANCH_COLORS[
    ((index % BRANCH_COLORS.length) + BRANCH_COLORS.length) % BRANCH_COLORS.length
  ];
}

/**
 * USER-PICKABLE node colors — the node toolbar's palette (Figma 275:14208).
 * Every hue is a DS ramp from the shared tailwind preset; swatches render the
 * 400 tone ringed by 500 (primary rings 950, per the Figma variables). A
 * picked color is stored on the logical node (`MindmapNode.color`) and wins
 * over the automatic branch hue everywhere that hue shows up: node fill,
 * incoming edge, and the split-view highlight/tint.
 */
export type NodeColor = BranchColor & {
  /** Palette swatch circle (24px, Figma Ellipse 400-fill + 500-ring). */
  swatch: string;
  /** Filled style for a recolored ROOT node (keeps the filled root look). */
  rootFill: string;
};

export const NODE_COLOR_ORDER = [
  'primary',
  'red',
  'rose',
  'orange',
  'amber',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'tertiary',
  'pink',
  'fuchsia',
  'secondary',
] as const;

export type NodeColorKey = (typeof NODE_COLOR_ORDER)[number];

// Spelled-out literal classes only — same JIT constraint as BRANCH_COLORS
// (Tailwind can't see `bg-${hue}-100`).
export const NODE_COLORS: Record<NodeColorKey, NodeColor> = {
  primary: {
    branch: 'border-primary-300 bg-primary-100',
    leaf: 'border-primary-200 bg-primary-50',
    edge: 'rgba(var(--primary-400-rgb), 1)',
    pdfHighlight: 'bg-primary-400/50',
    pdfTint: 'bg-primary-400/15',
    swatch: 'bg-primary-400 border border-primary-950',
    rootFill: 'border-primary-600 bg-primary-500 text-white',
  },
  red: {
    branch: 'border-red-300 bg-red-100',
    leaf: 'border-red-200 bg-red-50',
    edge: 'rgba(var(--red-400-rgb), 1)',
    pdfHighlight: 'bg-red-400/50',
    pdfTint: 'bg-red-400/15',
    swatch: 'bg-red-400 border border-red-500',
    rootFill: 'border-red-500 bg-red-400 text-white',
  },
  rose: {
    ...BRANCH_COLORS[6],
    swatch: 'bg-rose-400 border border-rose-500',
    rootFill: 'border-rose-500 bg-rose-400 text-white',
  },
  orange: {
    ...BRANCH_COLORS[1],
    swatch: 'bg-orange-400 border border-orange-500',
    rootFill: 'border-orange-500 bg-orange-400 text-white',
  },
  amber: {
    ...BRANCH_COLORS[4],
    swatch: 'bg-amber-400 border border-amber-500',
    rootFill: 'border-amber-500 bg-amber-400 text-white',
  },
  lime: {
    ...BRANCH_COLORS[7],
    swatch: 'bg-lime-400 border border-lime-500',
    rootFill: 'border-lime-500 bg-lime-400 text-white',
  },
  green: {
    branch: 'border-green-300 bg-green-100',
    leaf: 'border-green-200 bg-green-50',
    edge: 'rgba(var(--green-400-rgb), 1)',
    pdfHighlight: 'bg-green-400/50',
    pdfTint: 'bg-green-400/15',
    swatch: 'bg-green-400 border border-green-500',
    rootFill: 'border-green-500 bg-green-400 text-white',
  },
  emerald: {
    ...BRANCH_COLORS[2],
    swatch: 'bg-emerald-400 border border-emerald-500',
    rootFill: 'border-emerald-500 bg-emerald-400 text-white',
  },
  teal: {
    ...BRANCH_COLORS[5],
    swatch: 'bg-teal-400 border border-teal-500',
    rootFill: 'border-teal-500 bg-teal-400 text-white',
  },
  cyan: {
    branch: 'border-cyan-300 bg-cyan-100',
    leaf: 'border-cyan-200 bg-cyan-50',
    edge: 'rgba(var(--cyan-400-rgb), 1)',
    pdfHighlight: 'bg-cyan-400/50',
    pdfTint: 'bg-cyan-400/15',
    swatch: 'bg-cyan-400 border border-cyan-500',
    rootFill: 'border-cyan-500 bg-cyan-400 text-white',
  },
  sky: {
    ...BRANCH_COLORS[0],
    swatch: 'bg-sky-400 border border-sky-500',
    rootFill: 'border-sky-500 bg-sky-400 text-white',
  },
  tertiary: {
    branch: 'border-tertiary-300 bg-tertiary-100',
    leaf: 'border-tertiary-200 bg-tertiary-50',
    edge: 'rgba(var(--tertiary-400-rgb), 1)',
    pdfHighlight: 'bg-tertiary-400/50',
    pdfTint: 'bg-tertiary-400/15',
    swatch: 'bg-tertiary-400 border border-tertiary-500',
    rootFill: 'border-tertiary-500 bg-tertiary-400 text-white',
  },
  pink: {
    branch: 'border-pink-300 bg-pink-100',
    leaf: 'border-pink-200 bg-pink-50',
    edge: 'rgba(var(--pink-400-rgb), 1)',
    pdfHighlight: 'bg-pink-400/50',
    pdfTint: 'bg-pink-400/15',
    swatch: 'bg-pink-400 border border-pink-500',
    rootFill: 'border-pink-500 bg-pink-400 text-white',
  },
  fuchsia: {
    ...BRANCH_COLORS[3],
    swatch: 'bg-fuchsia-400 border border-fuchsia-500',
    rootFill: 'border-fuchsia-500 bg-fuchsia-400 text-white',
  },
  secondary: {
    branch: 'border-secondary-300 bg-secondary-100',
    // Leaf below branch, like every hue (-50 under -100). In dark the -50 is
    // the content surface itself (styles/elevation): a leaf adds no layer to
    // the map's pane, its rim is the edge — no half step.
    leaf: 'border-secondary-200 bg-secondary-50',
    edge: 'rgba(var(--secondary-400-rgb), 1)',
    pdfHighlight: 'bg-secondary-400/50',
    pdfTint: 'bg-secondary-400/15',
    swatch: 'bg-secondary-400 border border-secondary-500',
    rootFill: 'border-secondary-500 bg-secondary-400 text-white',
  },
};

/**
 * A zone's look in each hue (Figma Mindmaps 83:14747): the area is the -50
 * tint with a -200 border; the name badge above it is the same tint with
 * -600 text. Spelled out for the JIT, like the palettes above.
 */
export const ZONE_COLORS: Record<NodeColorKey, { area: string; badge: string }> = {
  primary: {
    area: 'border-primary-200 bg-primary-50',
    badge: 'bg-primary-50 text-primary-600',
  },
  red: { area: 'border-red-200 bg-red-50', badge: 'bg-red-50 text-red-600' },
  rose: { area: 'border-rose-200 bg-rose-50', badge: 'bg-rose-50 text-rose-600' },
  orange: {
    area: 'border-orange-200 bg-orange-50',
    badge: 'bg-orange-50 text-orange-600',
  },
  amber: { area: 'border-amber-200 bg-amber-50', badge: 'bg-amber-50 text-amber-600' },
  lime: { area: 'border-lime-200 bg-lime-50', badge: 'bg-lime-50 text-lime-600' },
  green: { area: 'border-green-200 bg-green-50', badge: 'bg-green-50 text-green-600' },
  emerald: {
    area: 'border-emerald-200 bg-emerald-50',
    badge: 'bg-emerald-50 text-emerald-600',
  },
  teal: { area: 'border-teal-200 bg-teal-50', badge: 'bg-teal-50 text-teal-600' },
  cyan: { area: 'border-cyan-200 bg-cyan-50', badge: 'bg-cyan-50 text-cyan-600' },
  sky: { area: 'border-sky-200 bg-sky-50', badge: 'bg-sky-50 text-sky-600' },
  tertiary: {
    area: 'border-tertiary-200 bg-tertiary-50',
    badge: 'bg-tertiary-50 text-tertiary-600',
  },
  pink: { area: 'border-pink-200 bg-pink-50', badge: 'bg-pink-50 text-pink-600' },
  fuchsia: {
    area: 'border-fuchsia-200 bg-fuchsia-50',
    badge: 'bg-fuchsia-50 text-fuchsia-600',
  },
  secondary: {
    area: 'border-secondary-200 bg-secondary-50',
    badge: 'bg-secondary-50 text-secondary-600',
  },
};

/** An "unstyled" edge (the line's "no colour" swatch): the text tone —
 *  near-black, and light in dark mode. */
export const UNSTYLED_EDGE_COLOR = 'rgba(var(--title-950-rgb), 1)';

/** The palette key of each automatic branch hue, slot for slot with
 *  BRANCH_COLORS (NODE_COLORS spreads the same entries under these keys). */
const BRANCH_KEYS: readonly NodeColorKey[] = [
  'sky',
  'orange',
  'emerald',
  'fuchsia',
  'amber',
  'teal',
  'rose',
  'lime',
];

/** The palette key of branch hue `index` (cycling, like `branchColor`). */
export function branchColorKey(index: number): NodeColorKey {
  return BRANCH_KEYS[
    ((index % BRANCH_KEYS.length) + BRANCH_KEYS.length) % BRANCH_KEYS.length
  ];
}

export function isNodeColorKey(value: unknown): value is NodeColorKey {
  return (
    typeof value === 'string' && (NODE_COLOR_ORDER as readonly string[]).includes(value)
  );
}

/** The hue a node actually renders with: its picked color, else its branch's. */
export function effectiveColor(
  colorKey: string | undefined,
  branchIndex: number,
): BranchColor {
  if (isNodeColorKey(colorKey)) return NODE_COLORS[colorKey];
  return branchColor(branchIndex);
}
